import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Ban } from "lucide-react";
import { Button } from "@akuna/ui";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import type { StayPlanModule } from "@/lib/daycare-hotel/stayPlans";
import { useCancelStayDay, useCancelStayReservation } from "./useCancelStay";

export interface StayCancelActionsProps {
  reservationId: string;
  stayDayId: string;
  module: StayPlanModule;
  stayDate: string;
  stayDayStatus?: string;
  hasFutureReservations?: boolean;
  onCancelled: () => void;
}

/** Abaixo da comanda: cancelar a reserva inteira ou só o acompanhamento do dia. */
export function StayCancelActions({ reservationId, stayDayId, module, stayDate, stayDayStatus, hasFutureReservations = false, onCancelled }: StayCancelActionsProps) {
  const { t } = useTranslation("daycareHotel");
  const cancelReservation = useCancelStayReservation();
  const cancelDay = useCancelStayDay();
  const [confirmCancellation, setConfirmCancellation] = useState(false);

  const runCancelReservation = async () => {
    try {
      await cancelReservation.mutateAsync({ reservationId, module, cancellationDate: stayDate });
      setConfirmCancellation(false);
      toast({ title: t("panel.cancelReservationDone") });
      onCancelled();
    } catch {
      toast({ variant: "destructive", title: t("panel.cancelReservationFailed") });
    }
  };

  const runCancelDay = async () => {
    try {
      await cancelDay.mutateAsync({ stayDayId, reservationId, module, stayDate, stayDayStatus });
      setConfirmCancellation(false);
      toast({ title: t("panel.cancelDayDone") });
      onCancelled();
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("panel.cancelDayFailed"),
        description: error instanceof Error ? error.message : String(error),
      });
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
      <Button
        variant="outline"
        size="sm"
        className="border-state-danger/40 text-state-danger hover:bg-state-danger-soft"
        onClick={() => setConfirmCancellation(true)}
      >
        <Ban aria-hidden="true" size={16} className="mr-1.5" />
        {t("panel.cancelReservation")}
      </Button>

      <AlertDialog
        open={confirmCancellation}
        onOpenChange={setConfirmCancellation}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("panel.cancelReservationTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("panel.cancelReservationScopeDescription")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row flex-nowrap items-center gap-2">
            <AlertDialogCancel className="m-0 whitespace-nowrap px-3 py-2 text-sm">{t("common:back")}</AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button className="whitespace-nowrap" size="sm" variant="destructive" onClick={() => void runCancelDay()}>
                {t("panel.cancelOnlyThisDay")}
              </Button>
            </AlertDialogAction>
            {hasFutureReservations ? (
              <AlertDialogAction asChild>
                <Button className="whitespace-nowrap" size="sm" variant="destructive" onClick={() => void runCancelReservation()}>
                  {t("panel.cancelAllDays")}
                </Button>
              </AlertDialogAction>
            ) : null}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
